<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { dateFormat } from 'shared/helpers/timeHelper';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import SearchAPI from 'dashboard/api/search';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const MIN_QUERY_LENGTH = 2;

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { highlightContent } = useMessageFormatter();

const isOpen = ref(false);
const query = ref('');
const results = ref([]);
const isLoading = ref(false);
const hasSearched = ref(false);
const inputRef = ref(null);

const currentChat = computed(() => store.getters.getSelectedChat);

const escapeHtml = html => {
  const wrapper = document.createElement('p');
  wrapper.textContent = html || '';
  return wrapper.textContent;
};

const resultSnippet = result => {
  const content =
    result.content || result.content_attributes?.email?.subject || '';
  return highlightContent(
    escapeHtml(content),
    query.value,
    'searchkey--highlight'
  );
};

const runSearch = async () => {
  const q = query.value.trim();
  if (q.length < MIN_QUERY_LENGTH || !currentChat.value?.id) {
    results.value = [];
    hasSearched.value = false;
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await SearchAPI.messages({
      q,
      conversationId: currentChat.value.id,
    });
    results.value = data.payload?.messages || [];
  } catch (error) {
    results.value = [];
  } finally {
    isLoading.value = false;
    hasSearched.value = true;
  }
};

const debouncedSearch = useDebounceFn(runSearch, 350);

watch(query, debouncedSearch);

const close = () => {
  isOpen.value = false;
  query.value = '';
  results.value = [];
  hasSearched.value = false;
};

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    nextTick(() => inputRef.value?.focus());
  } else {
    close();
  }
};

const goToMessage = message => {
  router
    .replace({
      query: { ...route.query, messageId: message.id },
    })
    .catch(() => {});
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId: message.id });
};

const onKeydown = e => {
  if (e.key === 'Escape' && isOpen.value) {
    close();
  }
};

onMounted(() => {
  emitter.on(BUS_EVENTS.TOGGLE_CONVERSATION_SEARCH, toggle);
});

onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.TOGGLE_CONVERSATION_SEARCH, toggle);
});

watch(
  () => currentChat.value?.id,
  () => close()
);
</script>

<template>
  <div
    v-show="isOpen"
    class="absolute inset-x-0 top-0 z-40 flex flex-col max-h-[45%] bg-n-solid-1 shadow-lg"
    @keydown="onKeydown"
  >
    <div
      class="flex items-center gap-3 px-4 min-h-[3.5rem] flex-shrink-0 border-b border-n-weak"
    >
      <button
        type="button"
        class="flex items-center justify-center w-8 h-8 rounded-md bg-transparent border-0 cursor-pointer text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        :aria-label="t('CONVERSATION.SEARCH.CLOSE')"
        @click="close"
      >
        <fluent-icon icon="dismiss" size="18" />
      </button>
      <div class="flex items-center gap-2 flex-1 min-w-0">
        <fluent-icon icon="search" size="16" class="text-n-slate-11" />
        <input
          ref="inputRef"
          v-model="query"
          type="text"
          class="flex-1 min-w-0 bg-transparent border-0 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
          :placeholder="t('CONVERSATION.SEARCH.PLACEHOLDER')"
        />
      </div>
    </div>

    <div
      v-if="isLoading || hasSearched"
      class="flex-1 overflow-y-auto border-b border-n-weak"
    >
      <div v-if="isLoading" class="flex items-center justify-center py-6">
        <Spinner class="text-n-brand" />
      </div>
      <div
        v-else-if="!results.length"
        class="px-3 py-6 text-sm text-center text-n-slate-11"
      >
        {{ t('CONVERSATION.SEARCH.NO_RESULTS') }}
      </div>
      <ul v-else class="py-1">
        <li v-for="message in results" :key="message.id">
          <button
            type="button"
            class="w-full text-left px-4 py-2 bg-transparent border-0 cursor-pointer hover:bg-n-alpha-1 flex flex-col gap-0.5"
            @click="goToMessage(message)"
          >
            <span
              v-dompurify-html="resultSnippet(message)"
              class="text-sm text-n-slate-12 line-clamp-2 [&_.searchkey--highlight]:font-semibold [&_.searchkey--highlight]:text-n-brand"
            />
            <span class="text-xs text-n-slate-10">
              {{ dateFormat(message.created_at, 'MMM d, h:mm a') }}
            </span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
